import { Stack, Typography, InputAdornment, IconButton } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { DatePicker } from 'app/components/elements/DatePicker';
import { InputSelect } from 'app/components/elements/InputSelect';
import { InputText } from 'app/components/elements/InputText';
import { PrimaryTableHeader, TableCustom } from 'app/components/elements/TableCustom';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState, ChangeEvent } from 'react';
import { getIssuesConfig } from 'services/option';
import {
  DEFAULT_ISSUES_UI_CONFIG,
  getAdHocDueDateColor,
  IssuesUiConfig
} from 'utils/issueDueDateColor';
import { useIssuesAssignmentsTab } from '../hooks/useIssuesAssignmentsTab';
import { AdHocIssueDialog } from './AdHocIssueDialog';
import { RoomIssueDialog } from './RoomIssueDialog';
import { AssetIssueDialog } from './AssetIssueDialog';
import { MaintenanceIssueDialog } from './MaintenanceIssueDialog';
import { CriticalityChip } from './CriticalityChip';
import { getCriticalityDisplay } from 'utils/issueCriticality';
import { DEFAULT_CRITICALITY_LEVELS, toCriticalitySelectOptions } from 'utils/issueCriticality';
import {
  IssuesFiltersBar,
  IssuesFilterField,
  IssuesDateFilterField,
  IssuesSearchField,
  filterFieldSx
} from './IssuesFiltersBar';
import { IssuesTabHeader } from './IssuesTabHeader';

export const IssuesAssignmentsTab = () => {
  const [issueStack, setIssueStack] = useState<number[]>([]);
  const [roomDialogIssueId, setRoomDialogIssueId] = useState<number | null>(null);
  const [roomDialogSubmitIdHint, setRoomDialogSubmitIdHint] = useState<number | undefined>(
    undefined
  );
  const [assetDialogIssueId, setAssetDialogIssueId] = useState<number | null>(null);
  const [maintenanceDialogIssueId, setMaintenanceDialogIssueId] = useState<number | null>(null);
  const [issuesConfig, setIssuesConfig] = useState<IssuesUiConfig>(DEFAULT_ISSUES_UI_CONFIG);
  const [sort, setSort] = useState<{ field?: string; order?: string }>({
    field: 'actionByDate',
    order: 'asc'
  });
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [search, setSearch] = useState('');
  const {
    assignments,
    assigneeOptions,
    statusOptions,
    issueTypeOptions,
    clientOptions,
    apiFilters,
    issueTypeFilter,
    setIssueTypeFilter,
    clientNameFilter,
    setClientNameFilter,
    assignedToFilter,
    setAssignedToFilter,
    statusFilter,
    setStatusFilter,
    criticalityFilter,
    setCriticalityFilter,
    setKeywordFilter,
    clearFilters,
    setApiFilter,
    pagingInfo,
    handleChangePage,
    refreshAssignments,
    exportCsv,
    exporting
  } = useIssuesAssignmentsTab();

  const onSearch = useCallback(() => {
    setKeywordFilter(search);
  }, [search, setKeywordFilter]);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    clearFilters();
  }, [clearFilters]);

  const onSortHeader = useCallback(
    (sortField: string) => {
      setSort((prev) => {
        const isSame = prev.field === sortField;
        const nextOrder = isSame ? (prev.order === 'asc' ? 'desc' : 'asc') : 'asc';
        return { field: sortField, order: nextOrder };
      });
    },
    [setSort]
  );

  // Keep API params in sync with UI sort + date filters
  useEffect(() => {
    setApiFilter('orderBy', sort.field || undefined);
    setApiFilter('order', (sort.order as 'asc' | 'desc' | undefined) || undefined);
  }, [setApiFilter, sort.field, sort.order]);

  useEffect(() => {
    setApiFilter('dateFrom', dateFrom ? dateFrom.startOf('day').toISOString() : undefined);
  }, [setApiFilter, dateFrom]);

  useEffect(() => {
    setApiFilter('dateTo', dateTo ? dateTo.endOf('day').toISOString() : undefined);
  }, [setApiFilter, dateTo]);

  useEffect(() => {
    let cancelled = false;
    getIssuesConfig()
      .then((res) => {
        if (cancelled) return;
        setIssuesConfig(res?.config || DEFAULT_ISSUES_UI_CONFIG);
      })
      .catch(() => {
        if (!cancelled) setIssuesConfig(DEFAULT_ISSUES_UI_CONFIG);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const criticalityLevels = issuesConfig.criticalityLevels?.length
    ? issuesConfig.criticalityLevels
    : DEFAULT_CRITICALITY_LEVELS;

  const criticalityOptions = useMemo(
    () => [{ label: 'All', value: '' }, ...toCriticalitySelectOptions(criticalityLevels)],
    [criticalityLevels]
  );

  const hasActiveFilters = Object.keys(apiFilters).length > 0;

  const headers: PrimaryTableHeader[] = useMemo(
    () => [
      { label: 'Issue Type', key: 'issueType', width: 100, sort: 'issueType' },
      { label: 'Client Name', key: 'clientName', width: 150, sort: 'clientName' },
      { label: 'Main Location', key: 'locationL1', width: 160, sort: 'location' },
      { label: 'Sub 1', key: 'locationL2', width: 160, sort: 'location' },
      { label: 'Sub 2', key: 'locationL3', width: 160, sort: 'location' },
      { label: 'Vertical', key: 'locationL4', width: 160, sort: 'location' },
      { label: 'Room', key: 'locationL5', width: 160, sort: 'location' },
      { label: 'Description', key: 'description', width: 260, sort: 'description' },
      { label: 'Assigned By', key: 'assignedBy', width: 140, sort: 'assignedBy' },
      { label: 'Assigned To', key: 'assignedTo', width: 140, sort: 'assignedTo' },
      { label: 'Due Date', key: 'actionByDate', width: 130, sort: 'actionByDate' },
      { label: 'Criticality', key: 'criticality', width: 120 },
      { label: 'Status', key: 'status', width: 120, sort: 'status' },
      { label: 'Actions', key: 'actions', width: 140 }
    ],
    []
  );

  const splitLocationPath = useCallback((path: string | undefined) => {
    const raw = String(path || '').trim();
    if (!raw) return ['', '', '', '', ''] as const;
    const parts = raw
      .split('_')
      .map((p) => p.trim())
      .filter(Boolean);
    return [
      parts[0] || '',
      parts[1] || '',
      parts[2] || '',
      parts[3] || '',
      parts.slice(4).join('_') || ''
    ] as const;
  }, []);

  const rows = useMemo(
    () =>
      assignments.map((item) => {
        const [l1, l2, l3, l4, l5FromPath] = splitLocationPath(item.locationPathString);
        const l1Id = item.locationLevelIds?.l1Id;
        const l2Id = item.locationLevelIds?.l2Id;
        const l3Id = item.locationLevelIds?.l3Id;
        const l4Id = item.locationLevelIds?.l4Id;
        const l5Id = item.locationLevelIds?.l5Id;

        // For non-AdHoc rows, show their `location` as the leaf/room column
        const l5 = item.issueType === 'AdHoc' ? l5FromPath : item.location || '';

        return {
          issueType: {
            value: (
              <TextEllipsis
                text={item.issueType === 'Room' ? 'Location' : item.issueType}
                width="95%"
                active={apiFilters.issueType === item.issueType}
                onClick={() =>
                  setApiFilter(
                    'issueType',
                    apiFilters.issueType === item.issueType ? undefined : item.issueType
                  )
                }
              />
            )
          },
          clientName: {
            value: (
              <TextEllipsis
                text={item.clientName}
                width="95%"
                active={apiFilters.clientName === item.clientName}
                onClick={() =>
                  setApiFilter(
                    'clientName',
                    apiFilters.clientName === item.clientName ? undefined : item.clientName
                  )
                }
              />
            )
          },
          locationL1: {
            value: (
              <TextEllipsis
                text={l1 || '-'}
                width="95%"
                active={!!l1 && l1Id != null && apiFilters.locationId === l1Id}
                onClick={() => {
                  if (!l1 || l1Id == null) return;
                  setApiFilter('locationId', apiFilters.locationId === l1Id ? undefined : l1Id);
                }}
              />
            )
          },
          locationL2: {
            value: (
              <TextEllipsis
                text={l2 || '-'}
                width="95%"
                active={!!l2 && l2Id != null && apiFilters.locationId === l2Id}
                onClick={() => {
                  if (!l2 || l2Id == null) return;
                  setApiFilter('locationId', apiFilters.locationId === l2Id ? undefined : l2Id);
                }}
              />
            )
          },
          locationL3: {
            value: (
              <TextEllipsis
                text={l3 || '-'}
                width="95%"
                active={!!l3 && l3Id != null && apiFilters.locationId === l3Id}
                onClick={() => {
                  if (!l3 || l3Id == null) return;
                  setApiFilter('locationId', apiFilters.locationId === l3Id ? undefined : l3Id);
                }}
              />
            )
          },
          locationL4: {
            value: (
              <TextEllipsis
                text={l4 || '-'}
                width="95%"
                active={!!l4 && l4Id != null && apiFilters.locationId === l4Id}
                onClick={() => {
                  if (!l4 || l4Id == null) return;
                  setApiFilter('locationId', apiFilters.locationId === l4Id ? undefined : l4Id);
                }}
              />
            )
          },
          locationL5: {
            value: (
              <TextEllipsis
                text={l5 || '-'}
                width="95%"
                active={
                  apiFilters.locationId !== undefined &&
                  (item.issueType === 'AdHoc'
                    ? !!l5 && l5Id != null && apiFilters.locationId === l5Id
                    : item.locationId != null && apiFilters.locationId === item.locationId)
                }
                onClick={() => {
                  // AdHoc: filter by room-level id (l5Id). Room/Asset: filter by their locationId (room id).
                  const nextId =
                    item.issueType === 'AdHoc'
                      ? l5Id
                      : item.locationId != null
                        ? item.locationId
                        : undefined;
                  if (!nextId) return;
                  setApiFilter('locationId', apiFilters.locationId === nextId ? undefined : nextId);
                }}
              />
            )
          },
          description: {
            value: (
              <Typography whiteSpace="pre-line" width="95%">
                {item.description}
              </Typography>
            )
          },
          assignedBy: {
            value: <TextEllipsis text={item.assignedBy || '-'} width="95%" />
          },
          assignedTo: {
            value: (
              <TextEllipsis
                onClick={() =>
                  item.assignedToUserId != null &&
                  setApiFilter(
                    'assignedToUserId',
                    apiFilters.assignedToUserId === item.assignedToUserId
                      ? undefined
                      : item.assignedToUserId
                  )
                }
                text={item.assignedTo}
                width="95%"
                active={
                  apiFilters.assignedToUserId !== undefined &&
                  apiFilters.assignedToUserId === item.assignedToUserId
                }
              />
            )
          },
          actionByDate: {
            value: item.actionByDate ? (
              <Typography
                width="95%"
                color={getAdHocDueDateColor(item.actionByDate, item.status, issuesConfig)}>
                {item.actionByDate}
              </Typography>
            ) : (
              <TextEllipsis text="-" width="95%" />
            )
          },
          criticality: {
            value: (
              <CriticalityChip
                criticality={(() => {
                  const d = getCriticalityDisplay(
                    item.criticalityKey ?? undefined,
                    criticalityLevels
                  );
                  if (d) return d;
                  if (item.criticalityLabel) {
                    return {
                      key: item.criticalityKey ?? undefined,
                      label: item.criticalityLabel,
                      color: item.criticalityColor
                    };
                  }
                  return null;
                })()}
              />
            )
          },
          status: {
            value: (
              <TextEllipsis
                onClick={() =>
                  setApiFilter(
                    'status',
                    apiFilters.status === item.status ? undefined : item.status
                  )
                }
                text={item.status}
                width="95%"
                active={apiFilters.status === item.status}
              />
            )
          },
          actions: {
            value: (
              <Typography
                sx={{ cursor: 'pointer', fontWeight: 600 }}
                onClick={() => {
                  if (item.issueType === 'AdHoc') {
                    const id = Number(String(item.id).replace('adhoc-', ''));
                    if (!Number.isNaN(id)) setIssueStack([id]);
                    return;
                  }
                  if (item.issueType === 'Room' && item.issueId) {
                    setRoomDialogIssueId(item.issueId);
                    setRoomDialogSubmitIdHint(item.submitId);
                    return;
                  }
                  if (item.issueType === 'Asset' && item.issueId) {
                    setAssetDialogIssueId(item.issueId);
                    return;
                  }
                  if (item.issueType === 'Maintenance') {
                    const id = Number(String(item.id).replace('maintenance-', ''));
                    if (!Number.isNaN(id)) setMaintenanceDialogIssueId(id);
                    return;
                  }
                }}>
                Edit
              </Typography>
            )
          }
        };
      }),
    [assignments, apiFilters, criticalityLevels, issuesConfig, setApiFilter, splitLocationPath]
  );

  return (
    <Stack paddingTop={3} gap={2.5} width="100%">
      <IssuesTabHeader
        title="Assignments"
        description="Room, asset, and ad hoc issues with an assignee. Filter by type, client, user, status, criticality, or due date range."
      />

      <IssuesFiltersBar
        exporting={exporting}
        onExportCsv={() => exportCsv(criticalityLevels)}
        showClearFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        helperText="Export includes all rows matching the filters below (up to 10,000).">
        <IssuesSearchField onSearch={onSearch}>
          <InputText
            title="Search"
            placeholder="Keyword, location, description…"
            startAdornment={
              <InputAdornment position="end">
                <IconButton size="small" onClick={onSearch}>
                  <img alt="icon-search" src="/icons/icon_search.svg" width={20} />
                </IconButton>
              </InputAdornment>
            }
            inputProps={{
              value: search,
              onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setSearch(event.target.value);
              },
              onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (event.key === 'Enter') onSearch();
              },
              style: { minHeight: '46px', height: '46px', padding: 0 }
            }}
          />
        </IssuesSearchField>

        <IssuesFilterField xs={6} sm={4} md={3} lg={3}>
          <InputSelect
            title="Issue Type"
            value={issueTypeFilter}
            placeholder="All types"
            sx={filterFieldSx}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setIssueTypeFilter(String(e.target.value || ''))
            }
            options={[{ label: 'All', value: '' }, ...issueTypeOptions]}
          />
        </IssuesFilterField>

        <IssuesFilterField xs={6} sm={4} md={3} lg={3}>
          <InputSelect
            title="Client"
            value={clientNameFilter}
            placeholder="All clients"
            sx={filterFieldSx}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setClientNameFilter(String(e.target.value || ''))
            }
            options={[{ label: 'All', value: '' }, ...clientOptions]}
          />
        </IssuesFilterField>

        <IssuesFilterField xs={12} sm={6} md={4} lg={3}>
          <InputSelect
            title="Assigned To"
            value={assignedToFilter === '' ? '' : String(assignedToFilter)}
            placeholder="All assignees"
            sx={filterFieldSx}
            onChange={(e: SelectChangeEvent<unknown>) => {
              const v = e.target.value;
              setAssignedToFilter(v === '' ? '' : Number(v));
            }}
            options={[
              { label: 'All', value: '' },
              ...assigneeOptions.map((o) => ({ label: o.label, value: String(o.value) }))
            ]}
          />
        </IssuesFilterField>

        <IssuesFilterField xs={6} sm={4} md={3} lg={3}>
          <InputSelect
            title="Status"
            value={statusFilter}
            placeholder="All statuses"
            sx={filterFieldSx}
            onChange={(e: SelectChangeEvent<unknown>) => setStatusFilter(e.target.value as string)}
            options={[{ label: 'All', value: '' }, ...statusOptions]}
          />
        </IssuesFilterField>

        <IssuesFilterField xs={6} sm={4} md={3} lg={3}>
          <InputSelect
            title="Criticality"
            value={criticalityFilter}
            placeholder="All criticalities"
            sx={filterFieldSx}
            onChange={(e: SelectChangeEvent<unknown>) =>
              setCriticalityFilter(String(e.target.value || ''))
            }
            options={criticalityOptions}
          />
        </IssuesFilterField>

        <IssuesDateFilterField>
          <DatePicker
            title="Date From"
            value={dateFrom}
            onChange={(val) => setDateFrom(val)}
            maxDate={dateTo || undefined}
          />
        </IssuesDateFilterField>

        <IssuesDateFilterField>
          <DatePicker
            title="Date To"
            value={dateTo}
            onChange={(val) => setDateTo(val)}
            minDate={dateFrom || undefined}
          />
        </IssuesDateFilterField>
      </IssuesFiltersBar>

      <TableCustom
        isScrollX
        data={rows}
        headers={headers}
        onSortHeader={onSortHeader}
        sort={sort}
        handleChooseRow={() => {}}
        pagingInfo={pagingInfo}
        handleChangePage={handleChangePage}
      />

      <AdHocIssueDialog
        mode="edit"
        isOpen={issueStack.length > 0}
        issueId={issueStack[issueStack.length - 1]}
        canGoBack={issueStack.length > 1}
        onBack={() => setIssueStack((prev) => prev.slice(0, -1))}
        onClose={() => setIssueStack([])}
        onSaved={async () => {
          await refreshAssignments();
        }}
        onOpenIssue={(id) => setIssueStack((prev) => [...prev, id])}
      />
      <RoomIssueDialog
        isOpen={roomDialogIssueId != null}
        issueId={roomDialogIssueId ?? undefined}
        submitIdHint={roomDialogSubmitIdHint}
        onClose={() => {
          setRoomDialogIssueId(null);
          setRoomDialogSubmitIdHint(undefined);
        }}
        onSaved={refreshAssignments}
      />
      <AssetIssueDialog
        isOpen={assetDialogIssueId != null}
        issueId={assetDialogIssueId ?? undefined}
        onClose={() => setAssetDialogIssueId(null)}
        onSaved={refreshAssignments}
      />
      <MaintenanceIssueDialog
        isOpen={maintenanceDialogIssueId != null}
        issueId={maintenanceDialogIssueId ?? undefined}
        item={assignments.find((a) => a.issueId === maintenanceDialogIssueId) ?? null}
        onClose={() => setMaintenanceDialogIssueId(null)}
        onSaved={refreshAssignments}
      />
    </Stack>
  );
};
